import { Box, Chip, IconButton, TextField, Typography } from "@mui/material";
import { Modal, ModalBody } from "reactstrap";
import CloseIcon from "@mui/icons-material/Close";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { UsernameSelectField, styles } from "../../pages/MyTracks/styles";
import { EmailInviteTextField, useStyles } from "../../pages/MyTracks/styles";
import {
  GetEnterpriseAllUsersResponse,
  InvitedCollaborator,
  InviteTemplate,
} from "../../proto/api_pb";
import { InvitedCollaboratorState } from "../../proto/api_pb";
import { Track, Collaborator, User, TrackFile } from "../../proto/api_pb";
import { emailRegex } from "../constants";
import { useContext, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { AuthContext, UserDetailsContext } from "../../contexts";
import { UserDetailsContextInterface } from "../../contexts";
import { RpcError } from "grpc-web";
import { postTrackFile } from "../../services/trackService";
import { postTrack, sendInvites } from "../../services/trackService";
import useGetValidAccessToken from "../../hooks/useGetValidAccessToken";
import { collaboratorsModalStyles, shareModalStyles } from "./styles";
import {
  APP_BACKGROUND_COLOR,
  APP_COLOR,
  FONT_FAMILY,
  isEnterprise,
} from "../../theme";
import { TrackObj, VersionAction } from "../../pages/MyTracks/types";
import { getEnterpriseAllUsers } from "../../services/enterpriseService";
import CustomButton from "../CustomButton/CustomButton";

const CollabModal: React.FC<{
  openCollabsModal: boolean;
  handleCloseCollabsModal: () => void;
  trackToEdit: Track | TrackFile | undefined;
  handleFetchTrackFiles?: () => Promise<void>;
  handleClose?: (
    isSuccessful?: boolean,
    statusMessage?: string,
    trackobj?: TrackObj
  ) => void;
}> = ({
  openCollabsModal,
  handleCloseCollabsModal,
  trackToEdit,
  handleFetchTrackFiles,
  handleClose,
}) => {
  const classes = useStyles();
  const [collabVal, setCollabVal] = useState("");
  const [emailInvite, setEmailInvite] = useState("");
  const [emailInviteList, setEmailInviteList] = useState<string[]>([]);
  const [usersList, setUsersList] = useState<Array<User>>([]);
  const { allUsers } = useContext(UserDetailsContext);
  const { userName } =
    useContext<UserDetailsContextInterface>(UserDetailsContext);
  const { userId } = useContext(AuthContext);
  const [collaborators, setCollaborators] = useState<Array<Collaborator>>([]);
  const { getValidAccessToken } = useGetValidAccessToken();
  const [addedCollabIds, setAddedCollabIds] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [userNames, setUserNames] = useState<string[]>([]);
  const { enterprise, isEnterpriseAccount } = useContext(UserDetailsContext);

  useEffect(() => {
    let userL = allUsers.filter((user) => user.getUsername() !== userName);
    if (trackToEdit) {
      const fetchedCollabs = trackToEdit
        .getCollaboratorsList()
        .map((item) => item.getUsername());
      userL = userL.filter(
        (usr) => !fetchedCollabs.includes(usr.getUsername())
      );
      setCollaborators(trackToEdit.getCollaboratorsList());
    }
    setUsersList(userL);
  }, [allUsers, userName, trackToEdit]);

  const addCollaborator = (newCollaborator: string) => {
    if (newCollaborator) {
      if (
        collaborators.find(
          (collaborator) => collaborator.getUsername() === newCollaborator
        ) === undefined &&
        usersList.some((user) => user.getUsername() === newCollaborator)
      ) {
        const addedCollab = usersList.find(
          (user) => user.getUsername() === newCollaborator
        );

        if (addedCollab) {
          const newCollab = new Collaborator();
          newCollab.setUserId(addedCollab?.getUserId());
          newCollab.setUsername(addedCollab?.getUsername());
          newCollab.setProfilePicture(addedCollab.getProfilePicture());
          const updatedCollaborators = [...collaborators, newCollab];

          setAddedCollabIds([...addedCollabIds, addedCollab.getUserId()]);
          setCollaborators(updatedCollaborators);
        }

        const newUserList = usersList.filter(
          (user) => user.getUsername() !== newCollaborator
        );
        setUsersList([...newUserList]);
        setCollabVal("");
      } else {
        toast.info("Username does not exist");
      }
      return true;
    }
  };

  const removeCollaborator = (collaborator: Collaborator) => {
    const updatedCollaborators = collaborators.filter(
      (item) => item !== collaborator
    );
    setCollaborators(updatedCollaborators);

    // updating userIds list that is being passed in sendInvite
    const updatedUserIdList = addedCollabIds.filter(
      (item) => item !== collaborator.getUserId()
    );
    setAddedCollabIds([...updatedUserIdList]);

    const newUser = new User();
    newUser.setUsername(collaborator.getUsername());
    newUser.setUserId(collaborator.getUserId());

    const newUsersList = [...usersList, newUser];
    setUsersList([...newUsersList]);
  };

  const handleCollabInvites = (event: React.SyntheticEvent<Element, Event>) => {
    if (emailInvite) {
      if (emailInvite.match(emailRegex)) {
        setEmailInviteList([...emailInviteList, emailInvite]);
        setEmailInvite("");
      } else {
        toast.info("Invalid email address!");
      }
    }
  };

  const handleRemoveEmail = (email: string) => {
    const updateEmailList = emailInviteList.filter((item) => item !== email);
    setEmailInviteList(updateEmailList);
  };

  const handleConfirmCollabs = async () => {
    try {
      setLoading(true);
      const token = await getValidAccessToken();
      const track = trackToEdit;
      if (track) {
        track.setCollaboratorsList(collaborators);

        // adding the invited collabs to the track obj
        if (emailInviteList.length !== 0) {
          const invitedCollabList: InvitedCollaborator[] = [];

          // for each email (invitedCollab) creating the obj and
          // adding it to the list
          emailInviteList.forEach((email) => {
            const newInvitedCollab = new InvitedCollaborator();
            newInvitedCollab.setEmail(email);
            newInvitedCollab.setInvitedCollaboratorState(
              InvitedCollaboratorState.INVITED_COLLABORATOR_STATE_PENDING
            );
            invitedCollabList.push(newInvitedCollab);
          });
          track.setInvitedCollaboratorsList([
            ...trackToEdit.getInvitedCollaboratorsList(),
            ...invitedCollabList,
          ]);
        }

        if (enterprise?.id && track instanceof Track && isEnterpriseAccount) {
          track.setEnterpriseId(enterprise.id);
        }
        // set attribute in track obj before api call
        if (track instanceof Track && trackToEdit instanceof Track) {
          const postTrackResponse = await postTrack(track, token, userId);
          if (postTrackResponse instanceof RpcError) {
            setEmailInviteList([]);
            handleCloseCollabsModal();
            throw postTrackResponse;
          }
          // sending email invitations if new collab or email is added
          if (emailInviteList.length !== 0 || addedCollabIds.length !== 0) {
            await sendInvites(
              await getValidAccessToken(),
              emailInviteList,
              addedCollabIds,
              userId,
              `${window.location.origin}/track/${track.getId()}`,
              track.getTitle(),
              InviteTemplate.INVITE_TEMPLATE_COLLABORATE_TRACK
            );
          }
          if (handleClose) {
            handleClose(true, "Collaborators Updated", {
              track: postTrackResponse.getTrack(),
              trackUrl: "",
              isPlaying: false,
              versionCount: VersionAction.TrackEdited,
              latestVersion: undefined,
              notifications: undefined,
            });
          }
        }
        if (track instanceof TrackFile && trackToEdit instanceof TrackFile) {
          const postFileResponse = await postTrackFile(
            track,
            token,
            isEnterpriseAccount ? enterprise?.id : undefined
          );
          if (postFileResponse instanceof RpcError) {
            setEmailInviteList([]);
            handleCloseCollabsModal();
            throw postFileResponse;
          }
          // sending email invitations if new collab or email is added
          if (emailInviteList.length !== 0 || addedCollabIds.length !== 0) {
            await sendInvites(
              await getValidAccessToken(),
              emailInviteList,
              addedCollabIds,
              userId,
              `${window.location.origin}/my-files`,
              track.getFilename(),
              InviteTemplate.INVITE_TEMPLATE_COLLABORATE_FILE
            );
          }
        }
        setEmailInviteList([]);
        handleCloseCollabsModal();
      }
      // if we got to this modal from myfiles
      if (handleFetchTrackFiles) {
        handleFetchTrackFiles();
        toast.success("Collaborators updated");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const hasCollaboratorsChanged = (
    originalCollaborators: Collaborator[] | undefined,
    collabLists: Collaborator[]
  ): boolean => {
    if (originalCollaborators) {
      // Check if the length of both arrays is different
      if (originalCollaborators.length !== collabLists.length) {
        return true;
      }

      // Create copies of both arrays and sort them
      // Assuming each collaborator has a unique username for comparison
      const sortedOriginal = [...originalCollaborators].sort((a, b) =>
        a.getUsername().localeCompare(b.getUsername())
      );
      const sortedCollabLists = [...collabLists].sort((a, b) =>
        a.getUsername().localeCompare(b.getUsername())
      );

      // Check if any item at any position is different
      for (let i = 0; i < sortedOriginal.length; i++) {
        if (
          sortedOriginal[i].getUserId() !== sortedCollabLists[i].getUserId()
        ) {
          return true;
        }
      }
    }

    return false;
  };

  const hasChanged = useMemo(
    () =>
      hasCollaboratorsChanged(
        trackToEdit?.getCollaboratorsList(),
        collaborators
      ),
    [trackToEdit, collaborators]
  );

  useEffect(() => {
    const handleUserNames = async () => {
      const token = await getValidAccessToken();
      const res = await getEnterpriseAllUsers(token, enterprise?.id ?? "");
      if (res instanceof GetEnterpriseAllUsersResponse) {
        const uName = res
          .getUsersList()
          .filter(
            (user) =>
              !user.getUsername()?.includes("dummy_ommuse") &&
              user.getUsername() !== userName
          )
          .map((user) => user.getUsername() || user.getEmail());
        setUserNames(uName);
        setUsersList(
          res
            .getUsersList()
            .filter(
              (user) =>
                !user.getUsername()?.includes("dummy_ommuse") &&
                user.getUsername() !== userName
            )
        );
      }
    };
    handleUserNames();
  }, [enterprise?.id, getValidAccessToken, userName]);

  return (
    <Modal
      isOpen={openCollabsModal}
      toggle={handleCloseCollabsModal}
      style={{ marginTop: 100 }}
    >
      <ModalBody style={{ backgroundColor: APP_BACKGROUND_COLOR }}>
        <Box sx={shareModalStyles.shareIconContainer}>
          <img
            style={shareModalStyles.shareIcons}
            src="/assets/icons/TracksPageIcons/users-plus.svg"
            alt="icon"
          />
        </Box>
        <Typography sx={shareModalStyles.editCollaboratorContainer}>
          Edit Collaborators
        </Typography>
        <IconButton
          onClick={handleCloseCollabsModal}
          style={styles.postMessageModalIcon}
        >
          <CloseIcon style={{ color: APP_COLOR }} />
        </IconButton>

        <Box sx={collaboratorsModalStyles.userNameLabelContainer}>
          <Box sx={{ width: "80%" }}>
            <label
              htmlFor="newCollaboratorUser"
              style={styles.modalCreatorUsername}
            >
              {"Collaborator's Username"}
            </label>
            <UsernameSelectField
              freeSolo
              forcePopupIcon={false}
              isOptionEqualToValue={(option, value) => option === value}
              inputValue={collabVal}
              onInputChange={(
                event: React.SyntheticEvent<Element, Event>,
                value: string
              ) => {
                setCollabVal(value.trim());
              }}
              options={
                isEnterpriseAccount
                  ? userNames
                  : usersList.map((item) => item.getUsername()).sort()
              }
              renderInput={(params) => (
                <TextField
                  style={{
                    backgroundColor: APP_BACKGROUND_COLOR,
                    color: "white",
                  }}
                  {...params}
                  label=" "
                  InputLabelProps={{
                    shrink: false,
                  }}
                  classes={{ root: classes.customTextField }}
                />
              )}
              renderOption={(props, option: any) => (
                <li key={option} {...props} style={{ fontFamily: FONT_FAMILY }}>
                  {option}
                </li>
              )}
            />
          </Box>
          <CustomButton
            isDisabled={collabVal === ""}
            text="Add"
            onClick={() => addCollaborator(collabVal)}
            customStyles={collaboratorsModalStyles.customButtonStyles}
          />
        </Box>
        <Box style={collaboratorsModalStyles.collaboratorContainer}>
          <Box>
            {collaborators &&
              collaborators.length > 0 &&
              collaborators.map((collaborator, index) => (
                <Box
                  className="collaborator"
                  style={{ width: "100%" }}
                  key={index}
                >
                  <img
                    style={collaboratorsModalStyles.profilePic}
                    src={
                      collaborator.getProfilePicture()
                        ? collaborator.getProfilePicture()
                        : enterprise?.image
                    }
                    alt={collaborator.getUsername()}
                  />
                  <p style={{ fontFamily: FONT_FAMILY, flexGrow: 1 }}>
                    {collaborator.getUsername()}
                  </p>
                  {/* Owner */}
                  {collaborator.getUserId() === trackToEdit?.getOwnerId() && (
                    <Chip
                      label="Owner"
                      color="primary"
                      sx={{
                        marginRight:
                          trackToEdit?.getOwnerId() === userId ? 1 : 0,
                        borderRadius: "5px",
                        height: "25px",
                      }}
                    />
                  )}
                  {/* You */}
                  {collaborator.getUserId() === userId && (
                    <Chip
                      label="You"
                      color="info"
                      sx={{ borderRadius: "4px", height: "25px" }}
                    />
                  )}
                  {/* Owner and user (you) cannot be removed */}
                  {collaborator.getUserId() !== trackToEdit?.getOwnerId() && (
                    <IconButton
                      onClick={() => removeCollaborator(collaborator)}
                    >
                      <DeleteForeverIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
          </Box>
        </Box>

        {!isEnterprise && (
          <Box sx={collaboratorsModalStyles.inviteCollaboratorContainer}>
            <Box sx={{ width: "80%" }}>
              <label htmlFor="emailInvite" style={styles.modalCreatorUsername}>
                {"Invite Collaborator"}
              </label>
              <EmailInviteTextField
                hiddenLabel
                variant="outlined"
                classes={{ root: classes.customTextField }}
                placeholder="Send an invite email to a collaborator"
                value={emailInvite}
                onChange={(
                  event: React.ChangeEvent<
                    HTMLTextAreaElement | HTMLInputElement
                  >
                ) => setEmailInvite(event.target.value)}
              />
            </Box>
            <CustomButton
              isDisabled={emailInvite === ""}
              text="Add"
              onClick={handleCollabInvites}
              customStyles={collaboratorsModalStyles.customButtonStyles}
            />
          </Box>
        )}

        <Box style={{ marginBottom: 10, width: "100%" }}>
          <Box>
            {emailInviteList &&
              emailInviteList.length > 0 &&
              emailInviteList.map((email, index) => (
                <Box
                  className="collaborator"
                  style={{ width: "100%", height: 55 }}
                  key={index}
                >
                  <p style={{ fontFamily: FONT_FAMILY, flexGrow: 1 }}>
                    {email}
                  </p>

                  <IconButton onClick={() => handleRemoveEmail(email)}>
                    <DeleteForeverIcon />
                  </IconButton>
                </Box>
              ))}
          </Box>
          {/* if there are invited collabs with pending invitations */}

          <Box>
            {trackToEdit &&
              trackToEdit.getInvitedCollaboratorsList().length > 0 &&
              trackToEdit.getInvitedCollaboratorsList().map((item, index) => {
                if (
                  item.getInvitedCollaboratorState() ===
                  InvitedCollaboratorState.INVITED_COLLABORATOR_STATE_PENDING
                )
                  return (
                    <Box
                      className="collaborator"
                      style={{ width: "100%" }}
                      key={index}
                    >
                      <p style={{ fontFamily: FONT_FAMILY, flexGrow: 1 }}>
                        {item.getEmail()}
                      </p>
                      <Chip label="Sent" color="primary" />
                    </Box>
                  );
              })}
          </Box>
        </Box>
        <CustomButton
          text="Confirm"
          loading={loading}
          onClick={handleConfirmCollabs}
          isDisabled={emailInviteList.length === 0 && !hasChanged}
          customStyles={{ width: "100%" }}
        />
      </ModalBody>
    </Modal>
  );
};

export default CollabModal;
